import * as React from 'react';
import { FormGroup, TextArea } from '@payscale/design';
import { Employee } from 'nomst/Stores/Decision/employee';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { ICountryDecisionDispatcher } from 'typings/countryDecision.dispatcher';
import { AmountCode, ConversationEntityType } from 'typings/enums';
import { Amount as AmountBusinessLogic } from 'nomst/Stores/Decision/amount';
import { requestNewTitleCode, requestNewTitleText } from 'common/constants';
import { observer } from 'mobx-react';
import PromotionSummaryView from 'nomst/Components/Decision/EmployeeCardDetails/PromotionView/promotionSummaryView';
import CreatePromotionRequestView from 'nomst/Components/Decision/EmployeeCardDetails/PromotionView/createPromotionRequestView';
import { Conversation } from '@payscale/payscale-conversation-service/lib/';
import { dataManagerFactory } from 'utils/MobxDataManager/singletons';
import './promotionRequestView.scss';
import { User } from 'nomst/Stores/User/user';

export interface IPromotionRequestViewProps {
    employee: Employee;
    configuration: Configuration;
    countryDecisionDispatcher: ICountryDecisionDispatcher;
    isCycleLocked: boolean;
    disableInput?: boolean;
    user: Nullable<User>;
}

export interface IPromotionRequestState {
    shouldValidateForm: boolean;
    isRequestSent: boolean;
    isSending: boolean;
}

export class PromotionRequestView extends React.Component<IPromotionRequestViewProps, IPromotionRequestState> {

    requestNewTitleJob: IJobSummary = {
        code: requestNewTitleCode,
        jobCodeKey: null,
        title: requestNewTitleText
    } as unknown as IJobSummary;

    constructor(props: IPromotionRequestViewProps) {
        super(props);

        this.state = Object.assign({}, this.state, {
            shouldValidateForm: false,
            isRequestSent: false,
            isSending: false
        });
        this.autocompleteSearchJobs = this.autocompleteSearchJobs.bind(this);
        this.setPromotionSubmittedFalse = this.setPromotionSubmittedFalse.bind(this);
        this.getJobTitle = this.getJobTitle.bind(this);
        this.onMessageSent = this.onMessageSent.bind(this);
        this.handleSetComment = this.handleSetComment.bind(this);
        this.resetPromotionRequest = this.resetPromotionRequest.bind(this);
        this.submitPromotionRequest = this.submitPromotionRequest.bind(this);
        this.deletePromotionRequest = this.deletePromotionRequest.bind(this);
        this.createPromotionRequest = this.createPromotionRequest.bind(this);
    }

    render(): React.ReactNode {
        const { disableInput, employee } = this.props;
        const { promotionRequest } = employee;

        if (!promotionRequest || !promotionRequest.promotionJobList) {
            return null;
        }
        if (!this.promotionAmount || !this.promotionAmount.isEligible) {
            return (
                <div className="promotion-ineligible">
                    <img
                        alt=""
                        src={require('contents/ineligible.svg')}
                        className="ineligible-icon"
                    />
                    <p className="ineligible-promotion-text">
                        Employee is not eligible for a promotion at this time
                    </p>
                </div>
            );
        }

        return (
            <div className="promotion-request-container">
                {this.renderPromotionSection()}
                {this.renderPromotionSummarySection()}
                {this.renderInitialCommentSection()}
                <div className="promotion-button-container">
                    {this.renderRequestHasBeenSentAlert()}
                    {this.renderActionButtons(!!disableInput)}
                </div>
                {this.renderConversationSection()}
            </div>
        );
    }

    private renderPromotionSummarySection(): React.ReactNode {
        const { employee, configuration } = this.props;
        return (
            <PromotionSummaryView
                employee={employee}
                configuration={configuration}
            />
        );
    }

    private renderConversationSection(): React.ReactNode {
        const { employee } = this.props;
        const { promotionRequest } = employee;
        if (employee.hasPromotionRequest === false) {
            return null;
        }

        const conversationDataManager =  dataManagerFactory.getConversationDataManager();
        const employeeDataManager = dataManagerFactory.getEmployeeDataManager();

        return (
            <div
                className="conversation-section"
                key={promotionRequest.compoundEntityIdForComment}
            >
                <p>Comments</p>
                <div className="conversation-box">
                    <Conversation
                        accessToken={this.props.configuration.accessToken}
                        conversationUrl={conversationDataManager.baseUrl}
                        employeeUrl={employeeDataManager.baseUrl}
                        uploadFileSizeLimit={conversationDataManager.fileSizeUploadLimit}
                        fileExtensions={conversationDataManager.fileExtensions}
                        userId={this.props.configuration.userId}
                        employeeSourceId={this.props.employee.employeeSourceId}
                        masterAccountId={this.props.configuration.masterAccountId}
                        entityId={promotionRequest.compoundEntityIdForComment}
                        entityType={ConversationEntityType.PromotionRequest}
                        unreadMessageCount={0}
                        disabled={!!this.props.disableInput}
                        hidePeopleInChat={true}
                        onMessageSent={this.onMessageSent}
                        onMessagesRead={() => {}}
                    />
                </div>
            </div>
        );
    }

    private renderRequestHasBeenSentAlert(): React.ReactNode {
        const { isRequestSent } =  this.state;
        const className = isRequestSent ? '' : 'hidden';

        return (
            <div className={` sent-request-alert ${className}`}>
                <span
                    className="icon-check"
                />
                <span>Request Sent</span>
            </div>
        );
    }

    private renderActionButtons(disableInput: boolean): React.ReactNode {
        const { employee, isCycleLocked } = this.props;
        if (disableInput || isCycleLocked) {
            return null;
        }

        const buttons = employee.hasPromotionRequest ?
            this.renderUpdateButtons(disableInput) :
            this.renderSubmitButtons(disableInput);

        return (
            <div className="action-buttons">
                {buttons}
            </div>
        );
    }

    private renderSubmitButtons(disableInput: boolean): React.ReactNode {
        const { employee } = this.props;
        const { promotionRequest } = employee;
        const unableToSave = disableInput || this.state.isSending
            || !this.promotionRequestReadyToSubmit();

        return (
            <span>
                <button
                    disabled={unableToSave}
                    className="pxl-btn action-buttons__button action-buttons__button--reset"
                    onClick={this.resetPromotionRequest}
                >
                    Reset
                </button>
                <button
                    disabled={unableToSave}
                    className="pxl-btn pxl-btn--primary action-buttons__button action-buttons__button--save"
                    onClick={this.createPromotionRequest}
                >
                    Save
                </button>
                <i hidden={!promotionRequest.isProcessing} className="icon-animate-spin icon-spin" />
            </span>
        );
    }

    private resetPromotionRequest(): void {
        const { promotionRequest } = this.props.employee;
        this.setState({isSending: true});
        promotionRequest.resetRequest();
        this.setState({isSending: false});
    }

    private async submitPromotionRequest(): Promise<void> {
        const { employee } = this.props;
        this.setState({isSending: true});
        await employee.submitPromotionRequest();
        this.setState({isRequestSent: true});
        this.setState({isSending: false});
    }

    private async createPromotionRequest(): Promise<void> {
        const { employee } = this.props;
        this.setState({isSending: true});
        await employee.saveNewPromotionRequest();
        this.setState({isRequestSent: true});
        this.setState({isSending: false});
    }

    private renderUpdateButtons(disableInput: boolean): React.ReactNode {
        const { employee } = this.props;
        const { promotionRequest } = employee;
        const unableToUpdate = disableInput || this.state.isSending
            || !this.hasUnsavedChanges();

        return (
            <span>
                <button
                    disabled={disableInput}
                    className="pxl-btn action-buttons__button action-buttons__button--delete"
                    onClick={this.deletePromotionRequest}
                >
                    Delete Request
                </button>
                <button
                    disabled={unableToUpdate}
                    className="pxl-btn action-buttons__button action-buttons__button--reset"
                    onClick={this.resetPromotionRequest}
                >
                    Reset
                </button>
                <button
                    disabled={unableToUpdate}
                    className="pxl-btn pxl-btn--primary action-buttons__button action-buttons__button--update"
                    onClick={this.submitPromotionRequest}
                >
                    Update Request
                </button>
                <i hidden={!promotionRequest.isProcessing} className="icon-animate-spin icon-spin update-request" />
            </span>
        );
    }

    private async deletePromotionRequest(): Promise<void> {
        const { employee } = this.props;
        this.setState({isSending: true});
        await employee.deletePromotionRequest();
        this.setState({isSending: false});
    }

    private hasUnsavedChanges(): boolean {
        const { employee } = this.props;
        const { promotionRequest } = employee;
        const hasPromotionAmountUnsavedChanges =
            this.promotionAmount?.value !== promotionRequest.data.promotion;
        let hasJobUnsavedChanges = false;

        if (promotionRequest.proposedJobCode === requestNewTitleCode) {
            hasJobUnsavedChanges = promotionRequest.data.proposedJobTitle !== promotionRequest.proposedJobTitle;
        } else {
            hasJobUnsavedChanges = promotionRequest.data.jobCode !== promotionRequest.proposedJobCode;
        }

        return hasPromotionAmountUnsavedChanges
            || hasJobUnsavedChanges;
    }

    private renderPromotionSection(): React.ReactNode {
        const { employee, isCycleLocked, configuration, countryDecisionDispatcher } = this.props;
        return (
            <CreatePromotionRequestView
                employee={employee}
                configuration={configuration}
                isCycleLocked={isCycleLocked}
                countryDecisionDispatcher={countryDecisionDispatcher}
                disableInput={isCycleLocked}
            />
        );
    }

    private renderInitialCommentSection(): React.ReactNode {
        const { employee, disableInput, isCycleLocked } = this.props;
        const { promotionRequest } = employee;

        if (employee.hasPromotionRequest || isCycleLocked) {
            return null;
        }

        return (
            <div className="comment-section">
                <label htmlFor="postcomment" className="comment-section__label">
                    Why should {employee.displayName} be promoted?
                </label>
                <FormGroup>
                    <TextArea
                        id="postcomment"
                        value={promotionRequest.currentComment}
                        onChange={this.handleSetComment}
                        rows={2}
                        placeholder=""
                        disabled={disableInput}
                    />
                </FormGroup>
            </div>
        );
    }

    private promotionRequestReadyToSubmit(): boolean {
        const { promotionRequest } = this.props.employee;
        if ((!promotionRequest.proposedJobCode && !promotionRequest.proposedJobTitle) ||
            !promotionRequest.promotionAmount ||
            !promotionRequest.currentComment) {
            return false;
        }
        return (promotionRequest.proposedJobCode.length > 0 || promotionRequest.proposedJobTitle.length > 0) &&
            promotionRequest.promotionAmount.value >= 0 &&
            promotionRequest.currentComment.length > 0;
    }

    private handleSetComment(_: undefined, comment: string): void {
        const { promotionRequest } = this.props.employee;
        this.setState({shouldValidateForm: !comment.length});
        promotionRequest.setComment(comment);
    }

    private getJobTitle(job: IJobSummary): string {
        return job.title;
    }

    private autocompleteSearchJobs(searchText: string, resultLimit: number): Promise<IJobSummary[]> {
        const { promotionJobList } = this.props.employee.promotionRequest;

        if (!searchText || searchText.trim().length === 0) {
            return Promise.resolve(promotionJobList.slice(0, resultLimit));
        }

        const result = promotionJobList.filter(item => {
            return item.title.toLowerCase().indexOf(searchText.toLowerCase()) > -1;
        }).slice(0, resultLimit);

        result.push(this.requestNewTitleJob);
        return Promise.resolve(result);
    }

    private setPromotionSubmittedFalse(): void {
        this.setState({ isRequestSent: false });
    }

    private async onMessageSent(): Promise<void> {
        const { promotionRequest } = this.props.employee;
        await promotionRequest.onCommentSent();
    }

    private get promotionAmount(): NullOrUndefined<AmountBusinessLogic> {
        const { employee } = this.props;
        return employee.getAmount(AmountCode.Promotion);
    }
}

export default observer(PromotionRequestView);